
import { Component, Vue } from 'vue-property-decorator'
import {
  listEntitlementProducts,
  createEntitlementProduct,
  updateEntitlementProduct,
  deleteEntitlementProduct,
  testProductResolution,
  EntitlementProduct
} from '@/api/entitlementProducts'
import Pagination from '@/components/Pagination/index.vue'

@Component({
  name: 'EntitlementProducts',
  components: {
    Pagination
  }
})
export default class extends Vue {
  private products: EntitlementProduct[] = []
  private total = 0
  private listLoading = false
  private listQuery = {
    page: 1,
    limit: 20,
    platform: undefined,
    isActive: undefined,
    search: undefined
  }

  private regionTags: string[] = []
  private regionInput = ''

  private dialogFormVisible = false
  private dialogStatus = 'create'
  private formData: any = {
    name: '',
    description: '',
    platform: '',
    productId: '',
    basePlanId: '',
    offerId: '',
    sourceType: '',
    featureGrants: [],
    metadataString: '',
    isActive: true,
    region: '',
    // Display fields for mobile app
    showInApp: false,
    code: '',
    title: '',
    buttonText: '',
    packageType: null,
    displayOrder: null,
    priceAmount: null,
    priceCurrency: '',
    isFirstSubscription: false,
    isTest: false,
    testUsers: ''
  }

  private rules = {
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    platform: [{ required: true, message: 'Platform is required', trigger: 'change' }],
    productId: [{ required: true, message: 'Product ID is required', trigger: 'blur' }],
    sourceType: [{ required: true, message: 'Source Type is required', trigger: 'change' }],
    featureGrants: [{ required: true, message: 'At least one feature grant is required', trigger: 'change' }]
  }

  private testDialogVisible = false
  private testProduct: EntitlementProduct | null = null
  private testData = {
    basePlanId: '',
    offerId: ''
  }
  private testResult: any = null

  created() {
    this.getList()
  }

  private async getList() {
    this.listLoading = true
    try {
      const { data } = await listEntitlementProducts(this.listQuery)
      this.products = data.data.products
      this.total = data.data.pagination.total
    } catch (error) {
      console.error('Failed to load products:', error)
      this.$message.error('Failed to load products')
    } finally {
      this.listLoading = false
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.getList()
  }

  private resetForm() {
    this.formData = {
      name: '',
      description: '',
      platform: '',
      productId: '',
      basePlanId: '',
      offerId: '',
      sourceType: '',
      featureGrants: [],
      metadataString: '',
      isActive: true,
      region: '',
      // Display fields for mobile app
      showInApp: false,
      code: '',
      title: '',
      buttonText: '',
      packageType: null,
      displayOrder: null,
      priceAmount: null,
      priceCurrency: '',
      isFirstSubscription: false,
      isTest: false,
      testUsers: ''
    }
  }

  private addRegionTag() {
    const val = this.regionInput.replace(/,/g, '').trim().toUpperCase()
    if (val && !this.regionTags.includes(val)) {
      this.regionTags.push(val)
      this.formData.region = this.regionTags.join(',')
    }
    this.regionInput = ''
  }

  private removeRegionTag(tag: string) {
    this.regionTags = this.regionTags.filter(t => t !== tag)
    this.formData.region = this.regionTags.join(',')
  }

  private syncRegionTags() {
    const val = this.formData.region as string
    this.regionTags = val ? val.split(',').map((r: string) => r.trim().toUpperCase()).filter((r: string) => r.length > 0) : []
    this.regionInput = ''
  }

  private handleCreate() {
    this.resetForm()
    this.syncRegionTags()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as any).clearValidate()
    })
  }

  private handleEdit(row: EntitlementProduct) {
    this.formData = {
      id: row.id,
      name: row.name,
      description: row.description,
      platform: row.platform,
      productId: row.productId,
      basePlanId: row.basePlanId || '',
      offerId: row.offerId || '',
      sourceType: row.sourceType,
      featureGrants: (row.featureGrants || []).map((g: any) => {
        const metadata = g.metadata || {}
        // Extract weassistSeconds from metadata for weassist feature
        let weassistSeconds = 3600 // default 1 hour
        if (g.feature_key === 'weassist') {
          weassistSeconds = metadata.seconds || (metadata.minutes ? metadata.minutes * 60 : 3600)
        }
        return {
          ...g,
          metadataString: JSON.stringify(metadata),
          weassistSeconds
        }
      }),
      metadataString: JSON.stringify(row.metadata || {}),
      isActive: row.isActive,
      region: row.region || '',
      // Display fields for mobile app
      showInApp: row.showInApp || false,
      code: row.code || '',
      title: row.title || '',
      buttonText: row.buttonText || '',
      packageType: row.packageType || null,
      displayOrder: row.displayOrder,
      priceAmount: row.priceAmount,
      priceCurrency: row.priceCurrency || '',
      isFirstSubscription: row.isFirstSubscription || false,
      isTest: row.isTest || false,
      testUsers: row.testUsers || ''
    }
    this.syncRegionTags()
    this.dialogStatus = 'edit'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as any).clearValidate()
    })
  }

  private createProduct() {
    (this.$refs.dataForm as any).validate(async(valid: boolean) => {
      if (valid) {
        try {
          const data = this.prepareFormData()
          await createEntitlementProduct(data)
          this.$message.success('Product created successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error) {
          console.error('Failed to create product:', error)
          this.$message.error('Failed to create product')
        }
      }
    })
  }

  private updateProduct() {
    (this.$refs.dataForm as any).validate(async(valid: boolean) => {
      if (valid) {
        try {
          const data = this.prepareFormData()
          await updateEntitlementProduct(this.formData.id, data)
          this.$message.success('Product updated successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error) {
          console.error('Failed to update product:', error)
          this.$message.error('Failed to update product')
        }
      }
    })
  }

  private prepareFormData() {
    const featureGrants = this.formData.featureGrants.map((g: any) => {
      const grant: any = {
        feature_key: g.feature_key,
        duration_type: g.duration_type
      }
      if (g.duration_type !== 'permanent' && g.duration_value) {
        grant.duration_value = g.duration_value
      }
      // Handle weassist feature specially - use weassistSeconds field
      if (g.feature_key === 'weassist') {
        grant.metadata = { seconds: g.weassistSeconds || 0 }
      } else if (g.metadataString) {
        try {
          grant.metadata = JSON.parse(g.metadataString)
        } catch (e) {
          console.warn('Invalid JSON in grant metadata')
        }
      }
      return grant
    })

    let metadata = {}
    if (this.formData.metadataString) {
      try {
        metadata = JSON.parse(this.formData.metadataString)
      } catch (e) {
        console.warn('Invalid JSON in product metadata')
      }
    }

    return {
      name: this.formData.name,
      description: this.formData.description,
      platform: this.formData.platform,
      productId: this.formData.productId,
      basePlanId: this.formData.basePlanId || null,
      offerId: this.formData.offerId || null,
      sourceType: this.formData.sourceType,
      featureGrants,
      metadata,
      isActive: this.formData.isActive,
      region: this.formData.region || null,
      // Display fields for mobile app
      showInApp: this.formData.showInApp,
      code: this.formData.code || null,
      title: this.formData.title || null,
      buttonText: this.formData.buttonText || null,
      packageType: this.formData.packageType || null,
      displayOrder: this.formData.displayOrder,
      priceAmount: this.formData.priceAmount,
      priceCurrency: this.formData.priceCurrency || null,
      isFirstSubscription: this.formData.isFirstSubscription,
      isTest: this.formData.isTest,
      testUsers: this.formData.testUsers || null
    }
  }

  private async handleDelete(row: EntitlementProduct) {
    this.$confirm(`Delete product "${row.name}"?`, 'Warning', {
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      type: 'warning'
    }).then(async() => {
      try {
        await deleteEntitlementProduct(row.id)
        this.$message.success('Product deleted successfully')
        this.getList()
      } catch (error) {
        console.error('Failed to delete product:', error)
        this.$message.error('Failed to delete product')
      }
    })
  }

  private handleTest(row: EntitlementProduct) {
    this.testProduct = row
    this.testData = {
      basePlanId: row.basePlanId || '',
      offerId: row.offerId || ''
    }
    this.testResult = null
    this.testDialogVisible = true
  }

  private async runTest() {
    if (!this.testProduct) return

    try {
      const { data } = await testProductResolution(this.testProduct.id, this.testData)
      this.testResult = data.data
      this.$message.success('Test completed')
    } catch (error) {
      console.error('Test failed:', error)
      this.$message.error('Test failed')
    }
  }

  private addFeatureGrant() {
    this.formData.featureGrants.push({
      feature_key: '',
      duration_type: 'months',
      duration_value: 1,
      metadataString: '{}',
      weassistSeconds: 3600
    })
  }

  private removeFeatureGrant(index: number) {
    this.formData.featureGrants.splice(index, 1)
  }

  private onFeatureKeyChange(grant: any) {
    if (grant.feature_key === 'weassist') {
      // Initialize weassistSeconds from existing metadata or default to 3600 (1 hour)
      try {
        const metadata = JSON.parse(grant.metadataString || '{}')
        grant.weassistSeconds = metadata.seconds || metadata.minutes * 60 || 3600
      } catch (e) {
        grant.weassistSeconds = 3600
      }
    }
  }

  private onWeassistSecondsChange(grant: any) {
    // Update metadataString with seconds value
    grant.metadataString = JSON.stringify({ seconds: grant.weassistSeconds || 0 })
  }

  private formatSecondsToTime(seconds: number): string {
    if (!seconds) return '0 seconds'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    const parts = []
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`)
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`)
    if (secs > 0) parts.push(`${secs} second${secs > 1 ? 's' : ''}`)
    return parts.join(' ') || '0 seconds'
  }

  private getPlatformType(platform: string) {
    const map: Record<string, string> = {
      general: 'warning',
      android: 'success',
      ios: 'primary',
      external: 'info'
    }
    return map[platform] || ''
  }

  private formatSourceType(sourceType: string) {
    const map: Record<string, string> = {
      app_store_purchase: 'App Store',
      google_play_purchase: 'Google Play',
      subscription_purchase: 'Subscription',
      zoho_purchase: 'Zoho',
      admin_grant: 'Admin',
      device_connection: 'Device',
      coupon_redemption: 'Coupon',
      referral_bonus: 'Referral',
      other: 'Other'
    }
    return map[sourceType] || sourceType
  }

  private getSourceTypeTag(sourceType: string) {
    const map: Record<string, string> = {
      app_store_purchase: 'primary',
      google_play_purchase: 'success',
      subscription_purchase: 'info',
      zoho_purchase: 'warning',
      admin_grant: 'danger',
      device_connection: 'info',
      coupon_redemption: 'success',
      referral_bonus: 'warning',
      other: ''
    }
    return map[sourceType] || ''
  }
}
